import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { OutlookLocation, OutlookRecipient } from "@shared/types/resource";
// import { useAuthStore } from "@/pages/taskpane/features/auth/store/authStore";
import { msalAdapter } from "@/pages/taskpane/features/auth/services/msalAdapter";
import { PERMISSION } from "@/pages/taskpane/features/auth/config/authConfig";
import { getUidSerieMasterByUidOccurrence, getMessageUrl } from "../utils";
import { getGraphData } from "../services/graph";
import { UidGenerator } from "@/pages/taskpane/lib/utils/UidGenerator";

// Office.js LocalClientTime type
export interface LocalClientTime {
  year: number;
  month: number;
  date: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
  timezoneOffset: number;
}

interface AppointmentTime {
  requiredAttendees: OutlookRecipient[];
  optionalAttendees: OutlookRecipient[];
  allAttendees: OutlookRecipient[];
  organizer: string;
  organizerName: string;
  subject: string;
  location: OutlookLocation[];
  enhancedLocation: OutlookLocation[];
  startTime: Date | null;
  endTime: Date | null;
  startDateTimeZone: LocalClientTime | null;
  endDateTimeZone: LocalClientTime | null;
  timeZone: string | null;
  error: string | null;
  recurringDates: {
    startDate: string;
    endDate: string;
  }[];
  isNoEndDateRecurring: boolean;
  itemId: string | null;
  restItemId: string | null;
  isReopenAppointment: boolean;
  isEditingRecurringSeries: boolean;
  iCalUId: string | null;
  customUid: string | null;
  globalId: string | null;
  guid: string | null;
  recurrence?: Office.Recurrence | null;
}

interface OutlookStore extends AppointmentTime {
  setRecurrence: (recurrence: Office.Recurrence | null) => void;
  // Time setters
  setAppointmentTime: (startTime: Date | null, endTime: Date | null) => void;
  setAppointmentTimeWithZone: (
    startTime: Date | null,
    endTime: Date | null,
    startDateTimeZone: LocalClientTime | null,
    endDateTimeZone: LocalClientTime | null
  ) => void;
  setTimeZone: (timeZone: string | null) => void;

  // Attendees setters
  setRequiredAttendees: (attendees: OutlookRecipient[]) => void;
  setOptionalAttendees: (attendees: OutlookRecipient[]) => void;
  setOrganizer: (organizer: string, organizerName: string) => void;

  // Content setters
  setSubject: (subject: string) => void;
  setLocation: (location: OutlookLocation[]) => void;
  setEnhancedLocation: (enhancedLocation: OutlookLocation[]) => void;

  // Utility setters
  setError: (error: string | null) => void;
  clearAll: () => void;

  // Getters for debugging
  getAll: () => AppointmentTime;

  // Validation methods
  isValid: () => boolean;
  getValidationErrors: () => string[];

  // Initialization
  initializeAppointmentTime: () => void;

  // Recurring dates setters
  setRecurringDates: (recurringDates: { startDate: string; endDate: string }[]) => void;

  setIsNoEndDateRecurring: (isEndDateRecurring: boolean) => void;

  // Item ID setters
  setItemId: (itemId: string | null) => void;
  setRestItemId: (restItemId: string | null) => void;
  setIsReopenAppointment: (isReopenAppointment: boolean) => void;
  setIsEditingRecurringSeries: (isEditingRecurringSeries: boolean) => void;

  // Initialize item ID
  initializeItemId: () => Promise<void>;
  setICalUId: (iCalUId: string | null) => void;
  setCustomUid: (customUid: string | null) => void;
  setGlobalId: (globalId: string | null) => void;
  setGuid: (guid: string | null) => void;
}

export const useOutlookStore = create<OutlookStore>()(
  devtools(
    (set, get) => ({
      requiredAttendees: [],
      optionalAttendees: [],
      allAttendees: [],
      organizer: "",
      organizerName: "",
      subject: "",
      location: [],
      enhancedLocation: [],
      startTime: null,
      endTime: null,
      timeZone: null,
      startDateTimeZone: null,
      endDateTimeZone: null,
      error: null,
      recurringDates: [],
      isNoEndDateRecurring: true,
      itemId: null,
      restItemId: null,
      isReopenAppointment: false,
      iCalUId: null,
      customUid: null,
      globalId: null,
      setTimeZone: (timeZone: string | null) => {
        // console.log("🌐 Store: Setting time zone", timeZone);
        set({ timeZone });
      },

      // Time setters
      setAppointmentTime: (startTime, endTime) => {
        // console.log("📅 Store: Setting appointment time", { startTime, endTime });
        set({ startTime, endTime });
      },

      setAppointmentTimeWithZone: (startTime, endTime, startDateTimeZone, endDateTimeZone) => {
        // console.log("📅 Store: Setting appointment time with timezone", { startTime, endTime, startDateTimeZone, endDateTimeZone });
        set({ startTime, endTime, startDateTimeZone, endDateTimeZone });
      },

      // Attendees setters
      setRequiredAttendees: (attendees) => {
        const { optionalAttendees } = get();
        const allAttendeesMap = new Map<string, OutlookRecipient>();

        [...attendees, ...optionalAttendees].forEach((att) => {
          if (att?.Email && !allAttendeesMap.has(att.Email)) {
            allAttendeesMap.set(att.Email, att);
          }
        });
        const allAttendees = Array.from(allAttendeesMap.values());

        set({ requiredAttendees: attendees, allAttendees });
      },

      setOptionalAttendees: (attendees) => {
        const { requiredAttendees } = get();
        const allAttendeesMap = new Map<string, OutlookRecipient>();

        [...requiredAttendees, ...attendees].forEach((att) => {
          if (att?.Email && !allAttendeesMap.has(att.Email)) {
            allAttendeesMap.set(att.Email, att);
          }
        });
        const allAttendees = Array.from(allAttendeesMap.values());

        set({ optionalAttendees: attendees, allAttendees });
      },

      setOrganizer: (organizer, organizerName) => {
        // console.log("🎯 Store: Setting organizer", organizer);
        set({ organizer, organizerName });
      },

      // Content setters
      setSubject: (subject) => {
        // console.log("📝 Store: Setting subject", subject);
        set({ subject });
      },

      setLocation: (location) => {
        console.log("📍 Store: Setting location", location);
        set({ location });
      },

      setEnhancedLocation: (enhancedLocation) => {
        console.log("🏢 Store: Setting enhanced location", enhancedLocation);
        set({ enhancedLocation });
      },

      // Utility setters
      setError: (error) => {
        // console.log("❌ Store: Setting error", error);
        set({ error });
      },

      setRecurringDates: (recurringDates: { startDate: string; endDate: string }[]) => {
        // console.log("🔄 Store: Setting recurring dates", recurringDates);
        set({ recurringDates });
      },

      setIsNoEndDateRecurring: (isNoEndDateRecurring: boolean) => {
        // console.log("🔄 Store: Setting is end date recurring", isNoEndDateRecurring);
        set({ isNoEndDateRecurring });
      },

      setItemId: (itemId: string | null) => {
        // console.log("🆔 Store: Setting item ID", itemId);
        set({ itemId });
      },

      setRestItemId: (restItemId: string | null) => {
        // console.log("🔄 Store: Setting REST item ID", restItemId);
        set({ restItemId });
      },

      setIsReopenAppointment: (isReopenAppointment: boolean) => {
        // console.log("🔄 Store: Setting is reopen appointment", isReopenAppointment);
        set({ isReopenAppointment });
      },

      setIsEditingRecurringSeries: (isEditingRecurringSeries: boolean) => {
        // console.log("🔄 Store: Setting is editing recurring series", isEditingRecurringSeries);
        set({ isEditingRecurringSeries });
      },

      setICalUId: (iCalUId: string | null) => {
        // console.log("🔄 Store: Setting iCalUId", iCalUId);
        set({ iCalUId });
      },
      setCustomUid: (customUid: string | null) => {
        // console.log("🔄 Store: Setting custom UID", customUid);
        set({ customUid });
      },
      setGlobalId: (globalId: string | null) => {
        // console.log("🔄 Store: Setting global ID", globalId);
        set({ globalId });
      },
      setGuid: (guid: string | null) => {
        // console.log("🔄 Store: Setting guid", guid);
        set({ guid });
      },
      clearAll: () => {
        // console.log("🧹 Store: Clearing all appointment data");
        set({
          requiredAttendees: [],
          optionalAttendees: [],
          allAttendees: [],
          organizer: "",
          organizerName: "",
          subject: "",
          location: [],
          enhancedLocation: [],
          startTime: null,
          endTime: null,
          startDateTimeZone: null,
          endDateTimeZone: null,
          timeZone: null,
          error: null,
          recurringDates: [],
          isNoEndDateRecurring: true,
          itemId: null,
          restItemId: null,
          isReopenAppointment: false,
          iCalUId: null,
          customUid: null,
          globalId: null,
        });
      },

      // Getters for debugging
      getAll: (): AppointmentTime => {
        const state = get();
        return {
          requiredAttendees: state.requiredAttendees,
          optionalAttendees: state.optionalAttendees,
          allAttendees: state.allAttendees,
          organizer: state.organizer,
          organizerName: state.organizerName,
          subject: state.subject,
          location: state.location,
          enhancedLocation: state.enhancedLocation,
          startTime: state.startTime,
          endTime: state.endTime,
          startDateTimeZone: state.startDateTimeZone,
          endDateTimeZone: state.endDateTimeZone,
          timeZone: state.timeZone,
          error: state.error,
          recurringDates: state.recurringDates,
          isNoEndDateRecurring: state.isNoEndDateRecurring,
          itemId: state.itemId,
          restItemId: state.restItemId,
          isReopenAppointment: state.isReopenAppointment,
          isEditingRecurringSeries: state.isEditingRecurringSeries,
          iCalUId: state.iCalUId,
          customUid: state.customUid,
          globalId: state.globalId,
          guid: state.guid,
        };
      },

      // Validation methods
      isValid: (): boolean => {
        const state = get();
        return !!(state.startTime && state.endTime && state.startTime < state.endTime);
      },

      getValidationErrors: (): string[] => {
        const state = get();
        const errors: string[] = [];

        if (!state.startTime) errors.push("Missing start time");
        if (!state.endTime) errors.push("Missing end time");
        if (state.startTime && state.endTime && state.startTime >= state.endTime) {
          errors.push("End time must be after start time");
        }
        if (state.subject.trim().length === 0) errors.push("Subject is required");

        return errors;
      },
      setRecurrence: (recurrence: Office.Recurrence | null) => {
        console.log("🔄 Store: Setting recurrence", recurrence);
        set({ recurrence });
      },

      initializeAppointmentTime: async () => {
        try {
          // console.log("🚀 Store: Initializing appointment time...");

          if (!Office || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
            console.error("❌ Store: Office.js context not available");
            set({ error: "Office.js context not available" });
            return;
          }

          const item = Office.context.mailbox.item;
          if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
            console.error("❌ Store: Not an appointment item");
            set({ error: "Not an appointment item" });
            return;
          }

          const appCompose = item as Office.AppointmentCompose;

          const [timeData] = await Promise.all([
            Promise.all([
              new Promise<Date>((resolve, reject) => {
                appCompose.start.getAsync((result) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    // console.log("✅ Store: Got start time:", result.value);
                    resolve(result.value);
                  } else {
                    console.error("❌ Store: Failed to get start time:", result.error);
                    reject(new Error(`Failed to get start time: ${result.error?.message}`));
                  }
                });
              }),
              new Promise<Date>((resolve, reject) => {
                appCompose.end.getAsync((result) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    // console.log("✅ Store: Got end time:", result.value);
                    resolve(result.value);
                  } else {
                    console.error("❌ Store: Failed to get end time:", result.error);
                    reject(new Error(`Failed to get end time: ${result.error?.message}`));
                  }
                });
              }),
            ]),
          ]);

          const [startTime, endTime] = timeData;

          // Convert to local client time
          let startDateTimeZone: LocalClientTime | null = null;
          let endDateTimeZone: LocalClientTime | null = null;

          try {
            if (startTime && Office?.context?.mailbox?.convertToLocalClientTime) {
              startDateTimeZone = Office.context.mailbox.convertToLocalClientTime(startTime);
              // console.log("🌐 Store: Converted start time to local:", startDateTimeZone);
            }

            if (endTime && Office?.context?.mailbox?.convertToLocalClientTime) {
              endDateTimeZone = Office.context.mailbox.convertToLocalClientTime(endTime);
              // console.log("🌐 Store: Converted end time to local:", endDateTimeZone);
            }
          } catch (error) {
            console.warn("⚠️ Store: Failed to convert to local client time:", error);
          }

          // Get timezone
          let userTimeZone = null;
          try {
            userTimeZone =
              Office.context.mailbox.userProfile.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            // console.log("🌍 Store: Got user timezone:", userTimeZone);
          } catch (error) {
            console.warn("⚠️ Store: Failed to get user timezone, using browser timezone:", error);
            userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
          }

          // console.log("📅 Store: Setting appointment times:", { startTime, endTime, startDateTimeZone, endDateTimeZone, timeZone: userTimeZone });
          // Set all state at once to avoid multiple re-renders
          set({ startTime, endTime, startDateTimeZone, endDateTimeZone, timeZone: userTimeZone, error: null });
        } catch (error) {
          console.error("❌ Store: Failed to initialize appointment time:", error);
          const errorMessage = error instanceof Error ? error.message : "Failed to get appointment time";
          set({ error: errorMessage });
        }
      },

      initializeItemId: async () => {
        function generateCustomUid(): Promise<string> {
          return new Promise((resolve) => {
            if (Office.context.mailbox.item) {
              Office.context.mailbox.item.sessionData.getAsync("customUid", (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                  if (asyncResult.value) {
                    const customUid = asyncResult.value as string;
                    resolve(customUid);
                  } else {
                    resolve("");
                  }
                } else if (Office.context.mailbox.item) {
                  const customUid = UidGenerator.NewUid();
                  Office.context.mailbox.item.sessionData.setAsync("customUid", customUid, (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                      resolve(customUid);
                    } else {
                      resolve("");
                    }
                  });
                } else {
                  resolve("");
                }
              });
            } else {
              resolve("");
            }
          });
        }
        try {
          // console.log("🆔 Store: Initializing item ID...");

          if (!Office || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
            console.error("❌ Store: Office.js context not available");
            set({ error: "Office.js context not available" });
            return;
          }

          const item = Office.context.mailbox.item;
          if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
            console.error("❌ Store: Not an appointment item");
            set({ error: "Not an appointment item" });
            return;
          }

          // Get item ID asynchronously
          item.getItemIdAsync(async (asyncResult) => {
            console.log("asyncResult", asyncResult);
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              const itemId = asyncResult.value;
              // console.log("✅ Store: Got item ID (EWS format):", itemId);

              // Convert to REST ID format for Graph API calls
              let restItemId: string | null = null;
              try {
                if (itemId && Office.context.mailbox.convertToRestId) {
                  restItemId = Office.context.mailbox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
                  // console.log("✅ Store: Converted to REST ID:", restItemId);
                }
              } catch (error) {
                console.warn("⚠️ Store: Failed to convert to REST ID:", error);
              }

              // If itemId exists, this is a saved/reopened appointment
              const isReopenAppointment = !!itemId;
              // console.log("🔄 Store: Is reopen appointment:", isReopenAppointment);

              set({
                itemId,
                restItemId,
                isReopenAppointment,
                error: null,
              });

              // Get ICalUId and customUid from Graph API
              const url = getMessageUrl(itemId);
              if (url) {
                try {
                  // Sử dụng msalAdapter.getValidToken() để đảm bảo token luôn hợp lệ và được refresh nếu cần
                  const accessToken = await msalAdapter.getValidToken(PERMISSION);
                  if (accessToken) {
                    try {
                      const data = await getGraphData(accessToken, url);
                      if (data) {
                        const iCalUId = data.iCalUId;
                        const customUid = getUidSerieMasterByUidOccurrence(iCalUId);
                        // console.log("✅ Store: Got iCalUId and customUid from Graph API:", { iCalUId, customUid });
                        set({ iCalUId, customUid });
                      }
                    } catch (error) {
                      console.error("❌ Store: Failed to get iCalUId and customUid:", error);
                      // Fallback: generate custom UID nếu Graph API call thất bại
                      const customUid = await generateCustomUid();
                      set({ customUid });
                    }
                  } else {
                    // Nếu không lấy được token hợp lệ, fallback về generate custom UID
                    console.warn("⚠️ Store: Could not get valid access token, using custom UID");
                    const customUid = await generateCustomUid();
                    set({ customUid });
                  }
                } catch (error) {
                  console.error("❌ Store: Failed to get valid token:", error);
                  // Fallback: generate custom UID nếu không lấy được token
                  const customUid = await generateCustomUid();
                  set({ customUid });
                }
              }
            } else {
              console.warn("⚠️ Store: Failed to get item ID (might be a new appointment):", asyncResult.error);

              // New appointment won't have itemId yet - generate a custom UID
              const customUid = await generateCustomUid();
              // console.log("🆕 Store: Generated custom UID for new appointment:", customUid);

              set({
                itemId: null,
                restItemId: null,
                isReopenAppointment: false,
                customUid,
                iCalUId: null,
                error: null,
              });
            }
          });
        } catch (error) {
          console.error("❌ Store: Failed to initialize item ID:", error);
          const errorMessage = error instanceof Error ? error.message : "Failed to get item ID";
          set({ error: errorMessage, itemId: null, restItemId: null, isReopenAppointment: false });
        }
      },
    }),
    {
      name: "outlook-store", // devtools name
    }
  )
);
